import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Params {
	enabled: boolean;
	onBlocked: (proceed: () => void) => void;
}

// The app mounts a BrowserRouter, so react-router's own blocker is unavailable:
// in-app exits from this page are anchors, and beforeunload covers a reload.
export const useUnsavedChangesGuard = ({ enabled, onBlocked }: Params) => {
	const navigate = useNavigate();

	useEffect(() => {
		if (!enabled) {
			return;
		}

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};

		const handleClick = (event: MouseEvent) => {
			if (
				event.defaultPrevented ||
				event.button !== 0 ||
				event.metaKey ||
				event.ctrlKey ||
				event.shiftKey ||
				event.altKey
			) {
				return;
			}
			if (!(event.target instanceof Element)) {
				return;
			}
			const anchor = event.target.closest('a[href]');
			if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank') {
				return;
			}
			const href = anchor.getAttribute('href') ?? '';
			if (!href.startsWith('/') || href === window.location.pathname) {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			onBlocked(() => navigate(href));
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		document.addEventListener('click', handleClick, true);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			document.removeEventListener('click', handleClick, true);
		};
	}, [enabled, navigate, onBlocked]);
};
